/* globals showModalInfo, showModalError */
import React, { useState, useEffect, useContext } from "react";
import ActionsContext from "common/ActionsContext";
import styled from "styled-components";
import Request from "common/Request";
import SpinnerInput from "common/SpinnerInput";
import SelectAutocomplete from "common/SelectAutocomplete";
import Labeled from "common/Labeled";
import Form from "common/Form";
import ColumnsGrid from "common/layouts/ColumnsGrid";
import { parseTimeSettings, parseTimezones } from "./api";
import { askConfirmation, mayDecline } from "common/AskConfirmation";

const ApplyButton = ({ what }) => (
  <div className="align-right">
    <button
      type="submit"
      className="btn btn-link waves-effect hidden-to-operators"
    >
      Apply {what}
    </button>
  </div>
);

const DateAndTimeLayout = styled.div`
  .input-group .form-line {
    border-bottom: 1px solid #ddd;
    flex-grow: 2;
  }
  .input-group.spinner,
  .input-group.spinner input {
    width: 100% !important;
  }
  .text-label.align-right {
    text-transform: capitalize;
  }
`;
const LabeledSpinnerInput = ({ ...settings }) => (
  <Labeled {...settings} className="align-right">
    <div className="margin-l-10 form-line">
      <SpinnerInput max={99} min={1} size={2} {...settings} />
    </div>
  </Labeled>
);

const doApplyDateAndTime = (settings) =>
  ifCl.run(`system date local ${formatDateAndTime(settings)}:00`);

const reportSuccess = (what) => () =>
  showModalInfo(`Successfully applied new system ${what}`, null);

const mayFail = (what) => (error) =>
  showModalError(`Error: Cannot apply new system ${what}`, error);

const applyDateAndTime = (settings) =>
  askConfirmation(
    "Confirm time change?",
    "Changing the time may cause a disruption in traffic of around 10 seconds. In any case, after a time change, it is recommended to perform a software reload of the active bqn package in Administration->Software",
    { icon: "info" }
  )
    .then(() => doApplyDateAndTime(settings))
    .then(reportSuccess("date & time"))
    .catch(mayDecline)
    .catch(mayFail("date & time"));

const DateAndTime = (settings) => (
  <DateAndTimeLayout>
    <Form onSubmit={applyDateAndTime}>
      <ColumnsGrid gap="1rem" columns={3} minWidth="6cm" rowGap="2rem">
        <LabeledSpinnerInput
          defaultValue={settings.year}
          name="year"
          label="year"
          max={9999}
          size={4}
          pad={false}
        />
        <LabeledSpinnerInput
          defaultValue={settings.month}
          name="month"
          label="month"
          size={2}
          max={12}
          pad={true}
        />
        <LabeledSpinnerInput
          defaultValue={settings.day}
          name="day"
          label="day"
          max={31}
          size={2}
          pad={true}
        />
        <LabeledSpinnerInput
          defaultValue={settings.hour}
          name="hour"
          label="hour"
          min={0}
          max={23}
          size={2}
          pad={true}
        />
        <LabeledSpinnerInput
          defaultValue={settings.minute}
          name="minute"
          label="minute"
          min={0}
          max={59}
          size={2}
          pad={true}
        />
        <ApplyButton what="Date and Time" />
      </ColumnsGrid>
    </Form>
  </DateAndTimeLayout>
);

const makeTwoDigits = (digit) => {
  const str = digit.toString();
  return (str.length === 1 ? `0${str}` : str)
};

const getDateAndTimeLabel = ({ year, month, day, hour, minute }) => 
  `${year}-${makeTwoDigits(month)}-${makeTwoDigits(day)} ${makeTwoDigits(
    hour
  )}:${makeTwoDigits(minute)}`;

const formatDateAndTime = ({ year, month, day, hour, minute }) =>
  `${year}-${month}-${day} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;

const doApplyTimezone = ({ timezone }) =>
  ifCl.run(`system date timezone ${timezone}`);

const applyTimezone = (settings) =>
  askConfirmation(
    "Confirm time change?",
    "Changing the time may cause a disruption in traffic of around 10 seconds. In any case, after a time change, it is recommended to perform a software reload of the active bqn package in Administration->Software",
    { icon: "info" }
  )
    .then(() => doApplyTimezone(settings))
    .then(reportSuccess("timezone"))
    .catch(mayDecline)
    .catch(mayFail("timezone"));

const TimeZone = ({ timezone }) => {
  const [request, setRequest] = useState(null);
  const actions = useContext(ActionsContext);

  const doRequest = () =>
    ifCl
      .run("show system date timezone")
      .then(parseTimezones)
      .then((options) => ({ options }));

  const doLoad = () => {
    setRequest(doRequest());
  };

  useEffect(() => (doLoad(), actions.recv("do-load", doLoad)), []);

  return request === null ? null : (
    <Request during={request}>
      {({ options }) => (
        <Form onSubmit={applyTimezone}>
          <ColumnsGrid gap="1rem" columns={3} minWidth="6cm" rowGap="2rem">
            <SelectAutocomplete
              className="take-2-columns"
              label="Time Zone"
              name="timezone"
              selected={timezone}
              options={options}
              inForm={true}
            />
            <ApplyButton what="Time Zone" />
          </ColumnsGrid>
        </Form>
      )}
    </Request>
  );
};

const onTimeoutAfter = (seconds, handler) => {
  const id = window.setTimeout(() => handler(), seconds * 1000);
  return /*cancel*/ () => window.clearTimeout(id);
};



const CurrentTime = ({ send, ...settings }) => {
  const dateAndTimeLabel = getDateAndTimeLabel(settings);
  const dateAndTime = formatDateAndTime(settings);
  const [showWarn, setShowWarn] = useState(false);
  const doShowWarn = () => setShowWarn(true);
  const doHideWarn = () => setShowWarn(false);
  useEffect(
    () => doHideWarn() || onTimeoutAfter(60, doShowWarn),
    [dateAndTime]
  );
  return (
    <div>
      <div className="input-group">
        <span className="input-group-addon">Current Date and Time:</span>
        <span className="input-group-addon">{dateAndTimeLabel}</span>
      </div>
      {showWarn !== true ? null : (
        <div className="input-group">
          <span
            className="input-group-addon col-red"
            style={{ cursor: "pointer" }}
            onClick={() => send("do-load")}
          >
            Time has changed! Click to update
          </span>
        </div>
      )}
    </div>
  );
};

const ListDateAndTime = () => {
  const [request, setRequest] = useState(null);
  const actions = useContext(ActionsContext);

  const sendSignal = (...args) => actions.send(...args);

  const doRequest = () =>
    ifCl.run("show system date full").then(parseTimeSettings);

  const doLoad = () => {
    setRequest(doRequest());
  };

  useEffect(() => (doLoad(), actions.recv("do-load", doLoad)), []);

  return request === null ? null : (
    <Request during={request}>
      {(result) => (
        <>
          <CurrentTime {...result} send={sendSignal} />
          <DateAndTime {...result} />
          <TimeZone {...result} />
        </>
      )}
    </Request>
  );
};

export default ListDateAndTime;
